"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.invalidCursorErrors = exports.paginationParams = exports.cursorParams = exports.spaceReadParams = exports.siloReadParams = exports.spaceParams = exports.siloParams = exports.readLocaleParam = exports.concreteLocaleParam = exports.boundedJsonSchema = exports.MAX_JSON_LENGTH = exports.emailString = exports.MAX_EMAIL_LENGTH = exports.MAX_ARRAY_LENGTH = exports.mediumText = exports.MAX_MEDIUM_TEXT_LENGTH = exports.urlString = exports.MAX_URL_LENGTH = exports.shortString = exports.MAX_SHORT_STRING_LENGTH = void 0;
exports.withMaxKeyCount = withMaxKeyCount;
exports.boundedRecord = boundedRecord;
exports.boundedJsonRecord = boundedJsonRecord;
exports.errorInvalidCursor = errorInvalidCursor;
/**
 * Reusable Zod field fragments for oRPC contract inputs.
 *
 * Each fragment is a plain record of Zod field schemas (not a `z.object()`)
 * so they spread cleanly into a procedure's input schema:
 *
 * ```ts
 * .input(z.object({ ...siloParams, ...cursorParams, status: z.string() }))
 * ```
 *
 * Use these for fields that appear across many procedures so the
 * validation rules (length, range, format) live in one place. Add a new
 * fragment here when a field starts to repeat across two or more
 * resources — keep one-offs co-located with their procedure.
 *
 * `shortString` / `urlString` / `mediumText` / `emailString` are ready-to-use
 * bounded schemas for the common cases — use one directly instead of writing
 * `z.string().max(MAX_SHORT_STRING_LENGTH)` at the call site; chain
 * `.optional()`/`.nullable()` as the field needs. `shortString`, `urlString`,
 * and `emailString` all reject the empty string — an identifier, key, URL, or
 * email is never meaningfully empty, so this doesn't need repeating at each
 * call site (`.optional()` still allows omitting the field entirely).
 * `urlString`/`emailString` go further and validate the value is a
 * well-formed URL/email, not just a bounded string. `mediumText` is the one
 * exception to both: it doesn't reject empty, since empty free text (an
 * appeal, a note) is legitimate — chain `.min(1)` yourself where non-empty
 * free text is required (e.g. `text: mediumText.min(1)`). Give a field its own
 * named schema (like `spaceNameSchema` in `procedures/spaces.ts`) instead
 * when its bound is independently meaningful rather than picked from a
 * shared tier. Only *inputs* need a bound — outputs are deliberately not
 * bounded (see `docs/architecture/orpc.md`); a repo-wide test
 * (`contract-length-bounds.test.ts`) walks every procedure's resolved input
 * schema and enforces this at the Zod level, not via a lint rule.
 *
 * `boundedRecord(valueSchema, maxKeys?)` is the standard shape for a
 * free-form map input — bounded by key count rather than serialized size, so
 * a client can't send one huge key or a million tiny ones. Counting keys is a
 * native `Object.keys().length` check, not a re-serialize-to-measure one.
 * `boundedJsonRecord` is its specialization for arbitrary JSON bags (e.g.
 * `comment.properties`) — each value is `boundedJsonSchema`, bounded by
 * total serialized size (see its own doc comment for why).
 */
const client_1 = require("@orpc/client");
const z = __importStar(require("zod"));
const locale_1 = require("../locale");
/**
 * Identifiers, keys, and short labels with no independent product meaning.
 * Never meaningfully empty, so this rejects the empty string; `.optional()`
 * still allows omitting the field entirely.
 */
exports.MAX_SHORT_STRING_LENGTH = 200;
exports.shortString = z.string().min(1).max(exports.MAX_SHORT_STRING_LENGTH);
/**
 * URLs. Validated as a well-formed absolute URL (`z.url()`), not just a
 * bounded string.
 */
exports.MAX_URL_LENGTH = 2000;
exports.urlString = z.url().min(1).max(exports.MAX_URL_LENGTH);
/**
 * Short/medium free text (notes, appeals) with no independent product
 * meaning. Unlike `shortString`/`urlString`, this does NOT reject the empty
 * string, since empty free text is legitimate. Chain `.min(1)` at the call
 * site when a field specifically requires non-empty text.
 */
exports.MAX_MEDIUM_TEXT_LENGTH = 5000;
exports.mediumText = z.string().max(exports.MAX_MEDIUM_TEXT_LENGTH);
/**
 * Element-count cap for list-shaped inputs (arrays of strings, keys, etc.),
 * and for record key counts (see `boundedRecord`).
 */
exports.MAX_ARRAY_LENGTH = 200;
/**
 * RFC 5321 mailbox length limit. No explicit `.min(1)` needed — `z.email()`'s
 * format check already rejects the empty string.
 */
exports.MAX_EMAIL_LENGTH = 254;
exports.emailString = z.email().max(exports.MAX_EMAIL_LENGTH);
/**
 * Total serialized-size cap for `boundedJsonSchema`. One number bounds both
 * data volume and nesting depth at once: `{"a":{"a":...}}` costs a few bytes
 * per level, so nothing can nest deep without also serializing long — unlike
 * bounding string/array/record leaves individually, which never looks at
 * depth at all.
 */
exports.MAX_JSON_LENGTH = 20000;
/**
 * Any JSON-serializable value (e.g. a comment's or space's free-form
 * `properties` bag), capped at `MAX_JSON_LENGTH` once serialized.
 * Re-serializing to measure would itself be a DoS vector against an
 * unbounded input, but isn't one here — `server/orpc/mount.ts`'s
 * whole-request body cap already bounds the worst case before this schema
 * ever runs.
 */
exports.boundedJsonSchema = z.json().refine((value) => JSON.stringify(value).length <= exports.MAX_JSON_LENGTH, {
    message: `Value exceeds ${exports.MAX_JSON_LENGTH} serialized characters`,
});
/**
 * Caps a record's key count — a client could otherwise send one huge key or
 * a million tiny ones. Checking `Object.keys(value).length` is a native,
 * O(key-count) check; it doesn't need to re-serialize the value to measure
 * it the way a total-byte-size check would.
 */
function withMaxKeyCount(schema, maxKeys) {
    return schema.refine((value) => Object.keys(value).length <= maxKeys, {
        message: `Value has more than ${maxKeys} keys`,
    });
}
/**
 * A `Record<string, V>` bounded by key count — the standard shape for any
 * free-form map input. Use this instead of writing
 * `withMaxKeyCount(z.record(z.string(), someValueSchema), maxKeys)` at the
 * call site.
 */
function boundedRecord(valueSchema, maxKeys = exports.MAX_ARRAY_LENGTH) {
    return withMaxKeyCount(z.record(z.string(), valueSchema), maxKeys);
}
/**
 * The shape of a free-form `properties` bag: any JSON-serializable record,
 * capped in key count. Used by `comments.properties`.
 */
function boundedJsonRecord(maxKeys = exports.MAX_ARRAY_LENGTH) {
    return boundedRecord(exports.boundedJsonSchema, maxKeys);
}
/**
 * The locale a call names for user-written text: the language to translate
 * into on a read, and the language the text is being authored in on a write.
 *
 * Optional while clients are still being migrated onto it: an omitting caller
 * is served every locale on a read, and authors in the request language on a
 * write. It becomes required once every client sends it.
 */
exports.concreteLocaleParam = z.enum(locale_1.ALL_ISO_LOCALES).optional().meta({
    description: 'The locale for user-written text in this call. On a write, the language the text is authored in. Falls back to the `Accept-Language` value when omitted.',
});
/**
 * `concreteLocaleParam` widened with `'*'`, meaning "every locale the content
 * has". Reads only: an editorial surface renders all languages at once, and
 * the teaser embed does its own subtag expansion, so both ask for the whole
 * map. A write has one authoring language, so `'*'` is meaningless there and
 * the write bundles keep the concrete schema.
 */
exports.readLocaleParam = z
    .union([z.enum(locale_1.ALL_ISO_LOCALES), z.literal('*')])
    .default('*')
    .meta({
    description: 'The locale to resolve user-written text into, or `*` for every locale the content has, which is the default.',
});
/**
 * `siloKey` + `locale` — every procedure must declare both.
 *
 * Carries the concrete locale, so a procedure that spreads this cannot accept
 * `'*'`. Reads spread `siloReadParams` instead; the asymmetry is deliberate,
 * and the failure mode of forgetting is a read that rejects `'*'`, never a
 * write that accepts it.
 */
exports.siloParams = {
    siloKey: exports.shortString,
    locale: exports.concreteLocaleParam,
};
/**
 * `siloKey` + `spaceKey` + `locale` — the identity of a single space (question
 * instance). Used by every space-scoped procedure.
 *
 * Every middleware stack in `server/orpc/procedures.ts` checks the space that
 * `spaceKey` names before the handler runs, so an unpublished space never
 * answers a caller who may not see it.
 */
exports.spaceParams = {
    ...exports.siloParams,
    spaceKey: exports.shortString.describe("The space's key within the silo. A draft space is visible to an admin only: to anyone else it reads as NOT_FOUND, the " +
        'same as a key that was never used.'),
};
/** `siloParams` for a read: `locale` widens to accept `'*'`. */
exports.siloReadParams = {
    ...exports.siloParams,
    locale: exports.readLocaleParam,
};
/** `spaceParams` for a read: `locale` widens to accept `'*'`. */
exports.spaceReadParams = {
    ...exports.spaceParams,
    locale: exports.readLocaleParam,
};
/**
 * How many items a paginated list returns at most. It defaults to 100 and caps
 * at 100000. The high cap is a stop-gap that lets "give me everything" callers
 * pull a whole collection in one page (see `ALL_COMMENTS_LIMIT`) until real
 * pagination lands in a follow-on PR.
 */
const limitParam = z.number().int().min(1).max(100000).default(100);
/**
 * Cursor pagination — `cursor` + `limit`. `INVALID_CURSOR` is the matching
 * typed error; spread `invalidCursorErrors` into the procedure's
 * `.errors({...})`.
 */
exports.cursorParams = {
    cursor: exports.shortString.optional(),
    limit: limitParam,
};
/**
 * Pagination — `offset` + `limit`, the inputs a list endpoint takes a page
 * with: skip `offset` items of the sorted, filtered list, then return up to
 * `limit`. Spread it into the procedure's input, and pair it with a `total` in
 * the output so a caller can tell how many pages there are.
 */
exports.paginationParams = {
    offset: z.number().int().min(0).default(0),
    limit: limitParam,
};
/**
 * Typed error for a cursor that doesn't resolve to a known position. Shared
 * across every paginated list endpoint — spread into `.errors({...})` on any
 * procedure that uses `cursorParams`, and throw `errorInvalidCursor` from the
 * server core when a cursor doesn't resolve. Error data is output, not
 * input, so it's deliberately left unbounded (see `docs/architecture/orpc.md`).
 */
exports.invalidCursorErrors = {
    INVALID_CURSOR: { status: 400, data: z.object({ cursor: z.string() }) },
};
function errorInvalidCursor(cursor) {
    return new client_1.ORPCError('INVALID_CURSOR', {
        status: 400,
        data: { cursor },
        message: 'The provided cursor is invalid: ' + cursor,
    });
}
//# sourceMappingURL=api-schemas.js.map